import { buildCourseUrl } from './courseUrl';
import { buildEventsListingUrl } from './eventsListingUrl';

/**
 * Represents a DatoCMS record target with a __typename discriminator
 * and optional fields depending on the record type.
 */
export type TargetRecord = { __typename: string } & Record<string, unknown>;

/**
 * Resolves a DatoCMS target record (from ButtonBlock or LinkBlock) to its
 * corresponding frontend URL path. Covers all members of
 * `ButtonBlockModelTargetField` and `LinkBlockModelTargetField` union types.
 *
 * @param target - The target record object containing at least `__typename`.
 * @param currentPathname - The current page pathname, used for context-dependent
 *   targets like `FormRecord`. Defaults to empty string.
 * @returns The resolved URL string, or `null` if the target type is unrecognized
 *   or the record lacks the required fields.
 */
export function resolveTargetUrl(
	target: TargetRecord,
	currentPathname: string = '',
): string | null {
	switch (target.__typename) {
		case 'CourseRecord':
			return target.slug && target.courseCode
				? buildCourseUrl(target.slug as string, target.courseCode as string)
				: null;

		case 'EditionRecord': {
			const course = target.course as { slug?: string; courseCode?: string } | undefined;
			return course?.slug && course?.courseCode
				? buildCourseUrl(course.slug, course.courseCode, target.productCode as string | undefined)
				: null;
		}

		case 'EventRecord':
			return target.slug ? `/eventi/${target.slug}` : null;
		
		case 'NewsRecord':
			return target.slug ? `/news/${target.slug}` : null;

		case 'InstructorRecord':
			return target.slug ? `/docenti/${target.slug}` : null;

		case 'ExternalLinkRecord':
			return (target.url as string) || null;

		case 'CoursesListingPageRecord':
			return target.slug ? `/${target.slug}` : null;

		case 'InstructorsListingPageRecord':
			return target.slug ? `/docenti/${target.slug}` : null;

		case 'PartnersListingPageRecord':
			return '/partners/all';

		case 'FaqPageRecord':
			return target.slug ? `/${target.slug}` : '/faq';

		case 'ContactPageRecord':
			return '/contattaci';

		case 'FormRecord':
			if (target.target === 'became-partner') {
				return '/partners/request-info?cta=became-partner';
			}
			return `${currentPathname}/request-info?cta=b2b-request-info`;

		case 'TargetPageRecord':
			return target.slug ? `/${target.slug}` : null;

		case 'HomePageRecord':
			return '/';

		case 'B2bPageRecord':
			return '/per-le-aziende';

		case 'B2bServicePageRecord':
			return target.slug ? `/${target.slug}` : null;

		case 'NewsAndEventsPageRecord':
			return '/news-ed-eventi';

		case 'EventsListingPageRecord':
			return buildEventsListingUrl(target.slug as string | undefined);
		
		case 'NewsListingPageRecord':
			return '/news';

		case 'FinancingPageRecord':
			return '/finanziamenti';

		case 'InstructorsPageRecord':
			return '/docenti';

		case 'PartnersPageRecord':
			return '/partners';

		case 'SuccessfulPartnerCaseRecord':
			return '/partners';

		default:
			return null;
	}
}
